import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar, EmptyState, StatusPill, BulkCheckbox,
  FilterDrawer, FilterSection, FilterChipGroup, ActiveFilters,
  SkeletonTable,
} from "../../components/ui";
import { Icon } from "../shared/components";
import {
  formatBrazilPhone, splitCommaTags,
  normalizeBrazilPhone, maskBrazilPhoneInput,
} from "../shared/utils";
import ContactModal from "./ContactModal";

/* ── Inline bulk bar ────────────────────────────────────────── */
function BulkBar({ count, onAddTag, onAssign, onCampaign, onExport, onDelete, onClear }) {
  if (count === 0) return null;
  return (
    <div className="contactsBulkBar">
      <span className="contactsBulkCount">{count} selecionado{count !== 1 ? "s" : ""}</span>
      <div className="contactsBulkActions">
        <button type="button" className="contactsBulkBtn" onClick={onAddTag}>
          <Icon name="tag" size={12} /> Adicionar tag
        </button>
        <button type="button" className="contactsBulkBtn" onClick={onAssign}>
          <Icon name="user" size={12} /> Atribuir
        </button>
        <button type="button" className="contactsBulkBtn" onClick={onCampaign}>
          <Icon name="send" size={12} /> Enviar campanha
        </button>
        <button type="button" className="contactsBulkBtn" onClick={onExport}>
          <Icon name="download" size={12} /> Exportar
        </button>
      </div>
      <div className="contactsBulkRight">
        <button type="button" className="contactsBulkBtn danger" onClick={onDelete}>
          <Icon name="trash" size={12} /> Excluir
        </button>
        <button type="button" className="contactsBulkClear" onClick={onClear} title="Limpar seleção">×</button>
      </div>
    </div>
  );
}

/* ── Info row ────────────────────────────────────────────────── */
function Info({ label, value }) { return <div className="infoItem"><span>{label}</span><strong>{value}</strong></div>; }

/* ── Main component ─────────────────────────────────────────── */
function ContactsPage({ api, onNavigate }) {
  const emptyForm = { name: "", phone: "", company: "", email: "", tags: "", notes: "" };
  const [contacts, setContacts]           = useState([]);
  const [search, setSearch]               = useState("");
  const [form, setForm]                   = useState(emptyForm);
  const [editingId, setEditingId]         = useState(null);
  const [loading, setLoading]             = useState(false);
  const [notice, setNotice]               = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [drawerMode, setDrawerMode]       = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  const [selectedIds, setSelectedIds]     = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm]     = useState(null);
  const [bulkTagging, setBulkTagging]     = useState(false);
  const [bulkTagValue, setBulkTagValue]   = useState("");
  const [bulkBusy, setBulkBusy]           = useState(false);
  const [filterOpen, setFilterOpen]       = useState(false);
  const [filters, setFilters]             = useState({ tags: [], hasCompany: null, hasEmail: null });
  const [draftFilters, setDraftFilters]   = useState({ tags: [], hasCompany: null, hasEmail: null });
  const [statusFilter, setStatusFilter]   = useState("all");

  // ContactModal
  const [contactModalOpen, setContactModalOpen]       = useState(false);
  const [contactModalMode, setContactModalMode]       = useState("create");
  const [contactModalContact, setContactModalContact] = useState(null);

  // Row 3-dot dropdown
  const [rowMenu, setRowMenu]     = useState(null); // { contact, x, y }
  const rowMenuRef                = useRef(null);

  // Quick tag from row dropdown
  const [quickTag, setQuickTag]   = useState(null); // { contact }
  const [quickTagVal, setQuickTagVal] = useState("");
  const quickTagRef               = useRef(null);

  /* ── Data loading ──────────────────────────────────────────── */
  async function loadContacts(value = search) {
    setLoading(true);
    try { const r = await api.get("/contacts", { params: { search: value } }); setContacts(r.data); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadContacts(""); }, []);

  /* ── Form helpers ──────────────────────────────────────────── */
  function updateField(field, value) { setForm((old) => ({ ...old, [field]: value })); }
  function resetForm() { setForm(emptyForm); setEditingId(null); setNotice(""); }
  function closeDrawer() { setDrawerMode(null); setActiveContact(null); resetForm(); }

  async function saveContact(e) {
    e.preventDefault(); setNotice("");
    if (!form.name.trim() || !form.phone.trim()) { setNotice("Nome e telefone são obrigatórios."); return; }
    const phoneDigits = normalizeBrazilPhone(form.phone);
    if (phoneDigits.length < 10 || phoneDigits.length > 13) { setNotice("Telefone inválido."); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setNotice("E-mail inválido."); return; }
    const payload = { ...form, phone: phoneDigits };
    try {
      if (editingId) { await api.put(`/contacts/${editingId}`, payload); setNotice("Contato atualizado."); }
      else { await api.post("/contacts", payload); setNotice("Contato criado."); }
      await loadContacts(); closeDrawer();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao salvar contato."); }
  }

  function editContact(contact) { setContactModalContact(contact); setContactModalMode("edit"); setContactModalOpen(true); }
  function openNewContact() { setContactModalContact(null); setContactModalMode("create"); setContactModalOpen(true); }
  function openContact(contact) { setActiveContact(contact); setDrawerMode("view"); setNotice(""); }

  async function deleteContact(contact) {
    try {
      await api.delete(`/contacts/${contact.id}`);
      setConfirmDelete(null); await loadContacts(); setNotice("Contato excluído.");
      if (activeContact?.id === contact.id) closeDrawer();
    } catch (err) { setConfirmDelete(null); setNotice(err.response?.data?.error || "Não foi possível excluir. Talvez exista conversa vinculada."); }
  }

  /* ── Abrir conversa: cria/reutiliza e navega ─────────────── */
  async function openConversation(contact) {
    try {
      const res = await api.post("/conversations", { contact_id: contact.id, status: "novo" });
      const convId = res.data?.id || res.data?.conversation_id;
      if (onNavigate && convId) {
        onNavigate("atendimentos", { conversationId: convId });
      } else {
        setNotice(res.data?.reused ? "Conversa existente — acesse o menu Conversas." : "Conversa criada — acesse o menu Conversas.");
      }
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao abrir conversa."); }
  }

  async function submitSearch(e) { e.preventDefault(); await loadContacts(search); }

  /* ── Bulk selection ────────────────────────────────────────── */
  function toggleSelected(id) {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  function toggleAll(checked) {
    if (checked) setSelectedIds(new Set(filteredContacts.map((c) => c.id)));
    else setSelectedIds(new Set());
  }
  function clearSelection() { setSelectedIds(new Set()); }

  async function bulkDelete() {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/contacts/bulk-delete", { ids: Array.from(selectedIds) });
      const { deleted = 0, skipped = 0 } = res.data || {};
      setBulkConfirm(null); clearSelection(); await loadContacts();
      setNotice(skipped > 0
        ? `${deleted} contato(s) excluído(s). ${skipped} ignorado(s) por possuírem conversas vinculadas.`
        : `${deleted} contato(s) excluído(s).`);
    } catch (err) { setNotice(err.response?.data?.error || "Erro na exclusão em lote."); }
    finally { setBulkBusy(false); }
  }

  async function bulkAddTag() {
    const tag = bulkTagValue.trim();
    if (!tag || selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/contacts/bulk-tag", { ids: Array.from(selectedIds), tag });
      const { updated = 0 } = res.data || {};
      setBulkTagging(false); setBulkTagValue(""); clearSelection(); await loadContacts();
      setNotice(`Tag "${tag}" aplicada a ${updated} contato(s).`);
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao aplicar tag em lote."); }
    finally { setBulkBusy(false); }
  }

  function bulkExport() {
    const rows = filteredContacts.filter((c) => selectedIds.has(c.id));
    const csv = ["Nome,Telefone,Empresa,Email,Tags", ...rows.map((c) => [c.name, c.phone, c.company || "", c.email || "", c.tags || ""].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "contatos.csv"; a.click();
    URL.revokeObjectURL(url); clearSelection();
  }

  /* ── Row 3-dot dropdown ────────────────────────────────────── */
  function openRowMenu(contact, e) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setRowMenu({ contact, x: rect.right - 180, y: rect.bottom + 4 });
  }
  function closeRowMenu() { setRowMenu(null); }

  useEffect(() => {
    if (!rowMenu) return;
    function onDoc(e) {
      if (rowMenuRef.current && !rowMenuRef.current.contains(e.target)) closeRowMenu();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [rowMenu]);

  async function rowCreateOpportunity(contact) {
    closeRowMenu();
    try {
      await api.post("/leads", { contact_id: contact.id, title: `Oportunidade — ${contact.name}`, status: "novo" });
      setNotice("Oportunidade criada no funil!");
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao criar oportunidade."); }
  }

  async function rowArchive(contact) {
    closeRowMenu();
    try {
      await api.put(`/contacts/${contact.id}`, { archived: true });
      await loadContacts(); setNotice("Contato arquivado.");
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao arquivar."); }
  }

  function rowDelete(contact) { closeRowMenu(); setConfirmDelete(contact); }

  /* ── Quick tag from dropdown ───────────────────────────────── */
  function openQuickTag(contact) { closeRowMenu(); setQuickTag({ contact }); setQuickTagVal(""); setTimeout(() => quickTagRef.current?.focus(), 50); }

  async function submitQuickTag(e) {
    e?.preventDefault();
    const tag = quickTagVal.trim();
    if (!tag) return;
    try {
      await api.post("/contacts/bulk-tag", { ids: [quickTag.contact.id], tag });
      await loadContacts(); setNotice(`Tag "${tag}" adicionada.`);
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao adicionar tag."); }
    finally { setQuickTag(null); setQuickTagVal(""); }
  }

  /* ── Filters ───────────────────────────────────────────────── */
  const allTagOptions = useMemo(() => {
    const counts = new Map();
    for (const c of contacts) {
      const tags = String(c.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
      for (const t of tags) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 24).map(([value, count]) => ({ value, label: value, count }));
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      if (filters.tags.length) {
        const tags = String(c.tags || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
        if (!filters.tags.some((t) => tags.includes(t.toLowerCase()))) return false;
      }
      if (filters.hasCompany === true && !c.company) return false;
      if (filters.hasCompany === false && c.company) return false;
      if (filters.hasEmail === true && !c.email) return false;
      if (filters.hasEmail === false && c.email) return false;
      if (statusFilter === "no_resp" && c.company) return false;
      return true;
    });
  }, [contacts, filters, statusFilter]);

  const allChecked = useMemo(() => {
    if (filteredContacts.length === 0) return false;
    if (selectedIds.size === 0) return false;
    const allSelected = filteredContacts.every((c) => selectedIds.has(c.id));
    if (allSelected) return true;
    return filteredContacts.some((c) => selectedIds.has(c.id)) ? "indeterminate" : false;
  }, [filteredContacts, selectedIds]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    filters.tags.forEach((t) => chips.push({ key: `tag:${t}`, label: `Tag: ${t}`, onRemove: () => setFilters((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) })) }));
    if (filters.hasCompany === true) chips.push({ key: "comp:y", label: "Com empresa", onRemove: () => setFilters((f) => ({ ...f, hasCompany: null })) });
    if (filters.hasCompany === false) chips.push({ key: "comp:n", label: "Sem empresa", onRemove: () => setFilters((f) => ({ ...f, hasCompany: null })) });
    if (filters.hasEmail === true) chips.push({ key: "mail:y", label: "Com email", onRemove: () => setFilters((f) => ({ ...f, hasEmail: null })) });
    if (filters.hasEmail === false) chips.push({ key: "mail:n", label: "Sem email", onRemove: () => setFilters((f) => ({ ...f, hasEmail: null })) });
    return chips;
  }, [filters]);

  function openFilters() { setDraftFilters(filters); setFilterOpen(true); }
  function applyFilters() { setFilters(draftFilters); setFilterOpen(false); }
  function resetFilters() { const e = { tags: [], hasCompany: null, hasEmail: null }; setDraftFilters(e); setFilters(e); }

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <>
      {/* ContactModal */}
      {contactModalOpen && (
        <ContactModal
          api={api}
          mode={contactModalMode}
          contact={contactModalContact}
          onClose={() => setContactModalOpen(false)}
          onSaved={() => { loadContacts(); }}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h3>Confirmar exclusão</h3>
            <p>Excluir o contato <strong>{confirmDelete.name}</strong>? Esta ação não pode ser desfeita.</p>
            <div className="modalActions">
              <button className="dangerBtn" onClick={() => deleteContact(confirmDelete)}>Excluir</button>
              <button className="secondaryBtn" onClick={() => setConfirmDelete(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirm */}
      {bulkConfirm && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h3>Excluir {selectedIds.size} contato(s)?</h3>
            <p>Contatos com conversas vinculadas serão ignorados. Esta ação não pode ser desfeita.</p>
            <div className="modalActions">
              <button className="dangerBtn" onClick={bulkDelete} disabled={bulkBusy}>{bulkBusy ? "Excluindo…" : "Excluir todos"}</button>
              <button className="secondaryBtn" onClick={() => setBulkConfirm(null)} disabled={bulkBusy}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk tag modal */}
      {bulkTagging && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h3>Aplicar tag a {selectedIds.size} contato(s)</h3>
            <label>Tag</label>
            <input autoFocus value={bulkTagValue} onChange={(e) => setBulkTagValue(e.target.value)} placeholder="Ex: vip, promo-junho" onKeyDown={(e) => e.key === "Enter" && bulkAddTag()} />
            <small className="fieldHint">A tag será adicionada sem remover as existentes.</small>
            <div className="modalActions">
              <button onClick={bulkAddTag} disabled={bulkBusy || !bulkTagValue.trim()}>{bulkBusy ? "Aplicando…" : "Aplicar tag"}</button>
              <button className="secondaryBtn" onClick={() => { setBulkTagging(false); setBulkTagValue(""); }} disabled={bulkBusy}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick tag modal (row dropdown) */}
      {quickTag && (
        <div className="modalOverlay" onClick={() => setQuickTag(null)}>
          <div className="modalBox" onClick={(e) => e.stopPropagation()}>
            <h3>Adicionar tag — {quickTag.contact.name}</h3>
            <form onSubmit={submitQuickTag}>
              <input ref={quickTagRef} value={quickTagVal} onChange={(e) => setQuickTagVal(e.target.value)} placeholder="Ex: vip, prioritario" />
              <small className="fieldHint">Tag única adicionada sem remover as existentes.</small>
              <div className="modalActions">
                <button type="submit" disabled={!quickTagVal.trim()}>Adicionar</button>
                <button type="button" className="secondaryBtn" onClick={() => setQuickTag(null)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Row 3-dot dropdown (fixed) */}
      {rowMenu && (
        <div
          ref={rowMenuRef}
          className="contactRowMenu"
          style={{ position: "fixed", top: rowMenu.y, left: rowMenu.x, zIndex: 9999 }}
        >
          <button type="button" className="contactRowMenuItem" onClick={() => { closeRowMenu(); window.open(`tel:${rowMenu.contact.phone}`, "_self"); }}>
            <Icon name="phone" size={13} /> Ligar
          </button>
          {rowMenu.contact.email && (
            <button type="button" className="contactRowMenuItem" onClick={() => { closeRowMenu(); window.open(`mailto:${rowMenu.contact.email}`, "_self"); }}>
              <Icon name="mail" size={13} /> Enviar email
            </button>
          )}
          <button type="button" className="contactRowMenuItem" onClick={() => openQuickTag(rowMenu.contact)}>
            <Icon name="tag" size={13} /> Adicionar tag
          </button>
          <button type="button" className="contactRowMenuItem" onClick={() => rowCreateOpportunity(rowMenu.contact)}>
            <Icon name="deal" size={13} /> Criar oportunidade
          </button>
          <div className="contactRowMenuDivider" />
          <button type="button" className="contactRowMenuItem" onClick={() => rowArchive(rowMenu.contact)}>
            <Icon name="eye-off" size={13} /> Arquivar
          </button>
          <button type="button" className="contactRowMenuItem danger" onClick={() => rowDelete(rowMenu.contact)}>
            <Icon name="trash" size={13} /> Excluir contato
          </button>
        </div>
      )}

      <div className="page-v2" style={{ paddingBottom: 32 }}>
        <div className="page-header-v2">
          <div>
            <div className="page-title-v2">Contatos</div>
            <div className="page-subtitle-v2">
              {loading
                ? "Carregando contatos…"
                : `${filteredContacts.length} de ${contacts.length} contato${contacts.length === 1 ? "" : "s"} · base ativa`}
            </div>
          </div>
          <div className="page-actions-v2">
            <button className="btn btn-secondary" type="button" title="Importar contatos (CSV)">↑ Importar</button>
            <button className="btn btn-secondary" type="button" title="Exportar contatos">↓ Exportar</button>
            <button className="btn btn-primary" onClick={openNewContact} type="button">
              <Icon name="plus" size={13} /> Novo contato
            </button>
          </div>
        </div>

        {notice && <div className="noticeBox" style={{ margin: "0 0 12px" }}>{notice}<button type="button" style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: "inherit" }} onClick={() => setNotice("")}>×</button></div>}

        {/* Bulk bar */}
        <BulkBar
          count={selectedIds.size}
          onAddTag={() => setBulkTagging(true)}
          onAssign={() => setNotice("Atribuição em lote em breve.")}
          onCampaign={() => setNotice("Campanha em lote em breve.")}
          onExport={bulkExport}
          onDelete={() => setBulkConfirm(true)}
          onClear={clearSelection}
        />

        <section className="contactsWorkspace">
          <div className="contactsTablePanel">
            <div className="contactsToolbar">
              <form onSubmit={submitSearch} className="contactsSearchForm">
                <Icon name="search" size={14} style={{ color: "var(--text-4)", flexShrink: 0 }} />
                <input
                  placeholder="Buscar por nome, telefone, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </form>
              <div className="contactsTabs">
                <button type="button" className={`contactsTab${statusFilter === "all" ? " active" : ""}`} onClick={() => setStatusFilter("all")}>Todos</button>
                <button type="button" className={`contactsTab${statusFilter === "online" ? " active" : ""}`} onClick={() => setStatusFilter("online")}>Online</button>
                <button type="button" className={`contactsTab${statusFilter === "no_resp" ? " active" : ""}`} onClick={() => setStatusFilter("no_resp")}>Sem responsável</button>
              </div>
              <div style={{ flex: 1 }} />
              <button type="button" className={`contactsToolbarBtn${activeFilterChips.length ? " is-active" : ""}`} onClick={openFilters}>
                <Icon name="filter" size={13} /> Filtros
                {activeFilterChips.length > 0 && <span className="contactsFilterBadge">{activeFilterChips.length}</span>}
              </button>
              <button type="button" className="contactsToolbarBtn">
                <Icon name="sort" size={13} /> Ordenar
              </button>
            </div>
            <ActiveFilters filters={activeFilterChips} onClear={() => setFilters({ tags: [], hasCompany: null, hasEmail: null })} />

            <div className="contactsTable">
              <div className="contactsTableHead">
                <span><BulkCheckbox checked={allChecked} onChange={toggleAll} /></span>
                <span>Nome</span>
                <span>Telefone</span>
                <span>Tags</span>
                <span>Status</span>
                <span>Responsável</span>
                <span>Último contato</span>
                <span>Ações</span>
              </div>
              {loading && contacts.length === 0 && <SkeletonTable rows={6} columns={5} withAvatar />}
              {!loading && filteredContacts.length === 0 && (
                <EmptyState
                  illustration={activeFilterChips.length ? "search" : "team"}
                  title={activeFilterChips.length ? "Nenhum contato corresponde aos filtros" : "Nenhum contato encontrado"}
                  text={activeFilterChips.length ? "Ajuste ou limpe os filtros aplicados." : "Cadastre o primeiro contato ou ajuste a busca."}
                />
              )}
              {filteredContacts.map((contact) => (
                <div className={`contactsTableRow${selectedIds.has(contact.id) ? " selected" : ""}`} key={contact.id}>
                  <span><BulkCheckbox checked={selectedIds.has(contact.id)} onChange={() => toggleSelected(contact.id)} /></span>
                  <button className="contactTablePrimary" onClick={() => openContact(contact)}>
                    <Avatar name={contact.name} className="photo" size="sm" />
                    <div>
                      <strong>{contact.name}</strong>
                      <small>{contact.company || "Sem empresa informada"}</small>
                    </div>
                  </button>
                  <span>{formatBrazilPhone(contact.phone)}</span>
                  <div className="tagsRow compact">
                    {splitCommaTags(contact.tags).length
                      ? splitCommaTags(contact.tags).slice(0, 2).map((tag) => <em key={tag}>{tag}</em>)
                      : <span className="tableMuted">Sem tags</span>}
                  </div>
                  <span><StatusPill tone="success">Ativo</StatusPill></span>
                  <span>{contact.company || "Sem responsável"}</span>
                  <span className="contactLastContact">{contact.notes ? "Com observação" : "—"}</span>
                  <div className="contactRowActions">
                    <button type="button" className="contactIconBtn" onClick={() => openConversation(contact)} title="Abrir conversa">
                      <Icon name="message" size={14} />
                    </button>
                    <button type="button" className="contactIconBtn" onClick={() => editContact(contact)} title="Editar contato">
                      <Icon name="edit" size={14} />
                    </button>
                    <button type="button" className="contactIconBtn" onClick={(e) => openRowMenu(contact, e)} title="Mais opções">
                      <Icon name="moreV" size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {drawerMode && (
            <aside className="contactsDrawer">
              <div className="contactsDrawerHeader">
                <div>
                  <strong>{drawerMode === "view" ? activeContact?.name || "Contato" : drawerMode === "new" ? "Novo contato" : "Editar contato"}</strong>
                  <span>{drawerMode === "view" ? "Dados completos do contato." : "Preencha os dados do contato."}</span>
                </div>
                <button type="button" className="ghostButton" onClick={closeDrawer}>Fechar</button>
              </div>
              {notice && <div className="noticeBox">{notice}</div>}
              {drawerMode === "view" && activeContact ? (
                <div className="contactsDrawerBody">
                  <div className="contactsDrawerHero">
                    <Avatar name={activeContact.name} className="photo" size="lg" />
                    <div>
                      <strong>{activeContact.name}</strong>
                      <span>{formatBrazilPhone(activeContact.phone)}</span>
                      <small>{activeContact.company || "Sem empresa informada"}</small>
                    </div>
                  </div>
                  <div className="drawerInfoGrid">
                    <Info label="Telefone" value={formatBrazilPhone(activeContact.phone)} />
                    <Info label="Empresa" value={activeContact.company || "Não informada"} />
                    <Info label="Email" value={activeContact.email || "Não informado"} />
                    <Info label="Tags" value={activeContact.tags || "Sem tags"} />
                  </div>
                  <div className="sideNote compactNote">
                    <strong>Observações</strong>
                    <p>{activeContact.notes || "Nenhuma observação cadastrada."}</p>
                  </div>
                  <div className="formActions stack">
                    <button type="button" onClick={() => openConversation(activeContact)}>Abrir conversa</button>
                    <button type="button" className="secondaryBtn" onClick={() => { closeDrawer(); editContact(activeContact); }}>Editar contato</button>
                    <button type="button" className="dangerBtn" onClick={() => setConfirmDelete(activeContact)}>Excluir contato</button>
                  </div>
                </div>
              ) : (
                <form className="contactFormPanel drawer" onSubmit={saveContact}>
                  <label>Nome *</label>
                  <input value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Ex: João Silva" />
                  <label>Telefone / WhatsApp *</label>
                  <input value={form.phone} onChange={(e) => updateField("phone", maskBrazilPhoneInput(e.target.value))} placeholder="Ex: +55 (83) 99999-9999" />
                  <small className="fieldHint">DDD + número — convertido automaticamente.</small>
                  <label>Empresa</label>
                  <input value={form.company} onChange={(e) => updateField("company", e.target.value)} placeholder="Ex: Empresa X" />
                  <label>Email</label>
                  <input value={form.email} onChange={(e) => updateField("email", e.target.value)} placeholder="cliente@email.com" />
                  <label>Tags</label>
                  <input value={form.tags} onChange={(e) => updateField("tags", e.target.value)} placeholder="quente, certificado, suporte" />
                  <label>Observações</label>
                  <textarea value={form.notes} onChange={(e) => updateField("notes", e.target.value)} placeholder="Anotações internas do atendimento..." />
                  <div className="formActions">
                    <button type="submit">{editingId ? "Salvar alterações" : "Cadastrar contato"}</button>
                    <button type="button" className="secondaryBtn" onClick={closeDrawer}>Cancelar</button>
                  </div>
                </form>
              )}
            </aside>
          )}
        </section>
      </div>

      <FilterDrawer open={filterOpen} onClose={() => setFilterOpen(false)} title="Filtros de contatos" onApply={applyFilters} onReset={resetFilters} activeCount={activeFilterChips.length}>
        <FilterSection title="Tags" hint={allTagOptions.length ? "Selecione uma ou mais (OR)" : "Nenhuma tag cadastrada."}>
          {allTagOptions.length > 0
            ? <FilterChipGroup options={allTagOptions} values={draftFilters.tags} onChange={(tags) => setDraftFilters((f) => ({ ...f, tags }))} />
            : <p className="fieldHint" style={{ margin: 0 }}>Cadastre tags em "Editar contato".</p>}
        </FilterSection>
        <FilterSection title="Empresa">
          <FilterChipGroup multi={false}
            options={[{ value: "yes", label: "Com empresa" }, { value: "no", label: "Sem empresa" }]}
            values={draftFilters.hasCompany === true ? ["yes"] : draftFilters.hasCompany === false ? ["no"] : []}
            onChange={(vals) => { const v = vals[0]; setDraftFilters((f) => ({ ...f, hasCompany: v === "yes" ? true : v === "no" ? false : null })); }}
          />
        </FilterSection>
        <FilterSection title="E-mail">
          <FilterChipGroup multi={false}
            options={[{ value: "yes", label: "Com e-mail" }, { value: "no", label: "Sem e-mail" }]}
            values={draftFilters.hasEmail === true ? ["yes"] : draftFilters.hasEmail === false ? ["no"] : []}
            onChange={(vals) => { const v = vals[0]; setDraftFilters((f) => ({ ...f, hasEmail: v === "yes" ? true : v === "no" ? false : null })); }}
          />
        </FilterSection>
      </FilterDrawer>
    </>
  );
}

export default ContactsPage;
